import React, { useState, useEffect } from 'react';
import {
    Paper, Typography, Chip, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Grid, TextField, MenuItem, Select, FormControl, 
    InputLabel, TablePagination, makeStyles, Box
} from '@material-ui/core';
import LoaderWithBackDrop from "../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import { getCreditsLedgerList } from "../../../../services/billing.service";
import { 
    CREDIT_TRANSACTION_TYPE, 
    CREDIT_TRANSACTION_TYPE_VERBOSE 
} from '../../../../utils/constants';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    table: {
        minWidth: 650,
    },
    filterContainer: {
        marginBottom: theme.spacing(3),
    },
    tableHeaderCell: {
        fontWeight: 600,
        backgroundColor: theme.palette.background.default,
    },
    amountPositive: {
        color: theme.palette.success.main,
        fontWeight: 'bold'
    },
    amountNegative: {
        color: theme.palette.error.main,
        fontWeight: 'bold'
    }
}));

const ExpandableText = ({ text, limit = 70 }) => {
    const [expanded, setExpanded] = React.useState(false);
    if (!text) return <span>—</span>;
    if (text.length <= limit) return <span>{text}</span>;

    return (
        <Box component="div" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            <Typography variant="body2" component="span">
                {expanded ? text : `${text.substring(0, limit)}... `}
            </Typography>
            <span 
                style={{ color: '#3f51b5', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, marginLeft: 4, display: 'inline-block' }}
                onClick={() => setExpanded(!expanded)}
            >
                {expanded ? 'Read Less' : 'Read More'}
            </span>
        </Box>
    );
};

const DQInsiderLedgerTab = () => {
    const classes = useStyles();

    // Data state
    const [loading, setLoading] = useState(false);
    const [ledgerEntries, setLedgerEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        transaction_type: '',
        page: 1,
        limit: 20
    });

    useEffect(() => {
        fetchData();
    }, [filters.page, filters.limit, filters.transaction_type]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { transaction_type, page, limit } = filters;
            const params = {
                page,
                limit,
                transaction_type: transaction_type || undefined
            };
            
            const result = await getCreditsLedgerList(params);
            
            if (result && result.data) {
                // The API response uses 'entries' for the list of records
                setLedgerEntries(result.data.entries || []);
                setTotal(result.data.total || 0);
            }

        } catch (error) {
            console.error('Error fetching all ledger:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1
        }));
    };

    const getTransactionTypeChip = (type) => {
        let color = 'default';
        switch (type) {
            case CREDIT_TRANSACTION_TYPE.TOPUP:      color = 'primary';   break;
            case CREDIT_TRANSACTION_TYPE.REFUND:     color = 'secondary'; break;
            case CREDIT_TRANSACTION_TYPE.ADJUSTMENT: color = 'default';   break;
            case CREDIT_TRANSACTION_TYPE.DEDUCTION:  color = 'default';   break;
        }
        return (
            <Chip 
                label={CREDIT_TRANSACTION_TYPE_VERBOSE[type] || type} 
                size="small" 
                color={color}
                variant={type === CREDIT_TRANSACTION_TYPE.ADJUSTMENT ? 'outlined' : 'default'}
            />
        );
    };

    return (
        <div>
            <Paper className={classes.paper} elevation={1}>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 600, marginBottom: 20 }}>
                    PBDQ Ledger List
                </Typography>

                <Grid container spacing={2} className={classes.filterContainer}>
                    <Grid item xs={12} sm={4}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Transaction Type</InputLabel>
                            <Select
                                value={filters.transaction_type}
                                onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
                                label="Transaction Type"
                            >
                                <MenuItem value=""><em>All Transactions</em></MenuItem>
                                {Object.keys(CREDIT_TRANSACTION_TYPE).map((key) => (
                                    <MenuItem key={key} value={CREDIT_TRANSACTION_TYPE[key]}>
                                        {CREDIT_TRANSACTION_TYPE_VERBOSE[CREDIT_TRANSACTION_TYPE[key]]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <TableContainer component={Paper} elevation={0}>
                    <Table className={classes.table} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeaderCell}>Insider ID</TableCell>
                                <TableCell className={classes.tableHeaderCell}>Date</TableCell>
                                <TableCell className={classes.tableHeaderCell}>Organization / Name</TableCell>
                                <TableCell className={classes.tableHeaderCell}>Type</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">Amount</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">Balance After</TableCell>
                                <TableCell className={classes.tableHeaderCell} style={{ minWidth: 200, maxWidth: 300 }}>Description</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {ledgerEntries.map((entry) => (
                                <TableRow key={entry._id}>
                                    <TableCell>
                                        <Typography variant="caption" style={{ fontWeight: 600 }}>
                                            {entry.dq_insider_id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
                                            {new Date(entry.createdAt).toLocaleString('en-GB', { 
                                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                            })}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                                            {entry.dq_insider_name || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {getTransactionTypeChip(entry.transaction_type)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <span className={entry.amount_credits >= 0 ? classes.amountPositive : classes.amountNegative}>
                                            {entry.amount_credits >= 0 ? '+' : ''}{entry.amount_credits}
                                        </span>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                            {entry.balance_after}
                                        </Typography>
                                    </TableCell>
                                    <TableCell style={{ maxWidth: 300 }}>
                                        <ExpandableText text={entry.description || entry.reason} />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {ledgerEntries.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" style={{ padding: 40 }}>
                                        <Typography color="textSecondary">No ledger entries found.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[20, 50, 100, 200]}
                    component="div"
                    count={total}
                    rowsPerPage={filters.limit}
                    page={filters.page - 1}
                    onPageChange={(e, newPage) => handleFilterChange('page', newPage + 1)}
                    onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
                />
                
                <LoaderWithBackDrop loading={loading} />
            </Paper>
        </div>
    );
};

export default DQInsiderLedgerTab;
